import { useEffect, useRef, useState } from "react";
import { twinAPI, fmtINR, fmtINRShort } from "./twinApi";
import CountUp from "@/pages/operations/CountUp";
import { toast } from "sonner";
import {
  Boxes, Warehouse, AlertTriangle, ArrowDownToLine, ArrowRightLeft,
  Package, ShoppingCart, CalendarClock, Snowflake, Search, X, Radio,
  Truck, ChevronRight, Zap, Flame, ShieldAlert, Sparkles,
} from "lucide-react";

const POLL_MS = 8000;

// ── Health palette ─────────────────────────────────────────────────────
const HEALTH = {
  healthy:  { ring: "ring-emerald-400/60", bar: "from-emerald-500 to-emerald-300", text: "text-emerald-300", chip: "bg-emerald-500/15 text-emerald-300", label: "Healthy" },
  low:      { ring: "ring-amber-400/60",   bar: "from-amber-500 to-amber-300",     text: "text-amber-300",   chip: "bg-amber-500/15 text-amber-300",     label: "Low" },
  critical: { ring: "ring-rose-400/70",    bar: "from-rose-500 to-rose-400",       text: "text-rose-300",    chip: "bg-rose-500/15 text-rose-300",       label: "Critical" },
  empty:    { ring: "ring-slate-400/40",   bar: "from-slate-500 to-slate-400",     text: "text-slate-300",   chip: "bg-slate-500/15 text-slate-300",     label: "Empty" },
  warning:  { ring: "ring-amber-400/60",   bar: "from-amber-500 to-amber-300",     text: "text-amber-300",   chip: "bg-amber-500/15 text-amber-300",     label: "Busy" },
};

export default function DigitalTwin() {
  const [overview, setOverview] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [storeMap, setStoreMap] = useState(null);
  const [openShelf, setOpenShelf] = useState(null);
  const [live, setLive] = useState([]);
  const [q, setQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchRes, setSearchRes] = useState(null);
  const searchTimer = useRef(null);

  // Fetch overview + live movements — with polling
  const refresh = async () => {
    try {
      const [ov, mv] = await Promise.all([twinAPI.overview(), twinAPI.movements(20)]);
      setOverview(ov);
      setLive(mv);
      if (!selectedId && ov.stores?.length) setSelectedId(ov.stores[0].id);
    } catch (e) { /* silent */ }
  };
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  // Fetch selected store map
  useEffect(() => {
    if (!selectedId) return;
    twinAPI.store(selectedId).then(setStoreMap).catch(() => setStoreMap(null));
    const t = setInterval(() => {
      if (selectedId) twinAPI.store(selectedId).then(setStoreMap).catch(() => {});
    }, POLL_MS);
    return () => clearInterval(t);
  }, [selectedId]);

  // Global keyboard shortcut for search: Cmd/Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === "Escape") setSearchOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Debounced search
  useEffect(() => {
    if (!searchOpen) { setSearchRes(null); return; }
    if (!q.trim()) { setSearchRes(null); return; }
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      twinAPI.search(q.trim()).then(setSearchRes).catch(() => setSearchRes(null));
    }, 200);
    return () => clearTimeout(searchTimer.current);
  }, [q, searchOpen]);

  const kpis = overview?.kpis || {};
  const stores = overview?.stores || [];
  const recent = overview?.recent || [];
  const lowWall = overview?.low_stock || [];

  return (
    <div className="min-h-screen bg-[#070B1F] on-dark text-white" data-testid="digital-twin">
      {/* Ambient glow layer */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-hocp-gold/[0.08] blur-3xl"/>
        <div className="absolute -bottom-32 -left-40 w-[500px] h-[500px] rounded-full bg-blue-500/[0.08] blur-3xl"/>
      </div>

      <div className="relative max-w-[1600px] mx-auto p-6 space-y-5">
        {/* HEADER ─────────────────────────────────── */}
        <header className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-hocp-gold to-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
            <Sparkles className="w-5 h-5 text-hocp-eerie" strokeWidth={2.25}/>
          </div>
          <div className="flex-1">
            <h1 className="font-display text-[26px] font-bold leading-none tracking-tight">Inventory Digital Twin</h1>
            <p className="text-[12.5px] text-white/50 mt-1.5 flex items-center gap-2">
              <Radio className="w-3 h-3 text-emerald-400 animate-pulse"/>
              Live mission control · updates every {POLL_MS / 1000}s
            </p>
          </div>
          <button onClick={() => setSearchOpen(true)} data-testid="dt-open-search"
            className="hidden md:flex items-center gap-2 px-3.5 py-2 rounded-xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] text-[12.5px] text-white/70 backdrop-blur-md">
            <Search className="w-3.5 h-3.5"/> Search items, vendors, stores
            <kbd className="ml-2 px-1.5 py-0.5 rounded bg-white/10 text-[10px]">⌘K</kbd>
          </button>
        </header>

        {/* KPI STRIP ───────────────────────────────── */}
        <KpiStrip kpis={kpis}/>

        {/* MAIN GRID ──────────────────────────────── */}
        <div className="grid grid-cols-1 xl:grid-cols-[240px_1fr_320px] gap-4">
          {/* LEFT · Store selector */}
          <StoreSelector stores={stores} selectedId={selectedId} onSelect={setSelectedId}/>

          {/* CENTER · Store visualization */}
          <div className="min-w-0 space-y-4">
            <StoreVisualization data={storeMap} onShelfClick={setOpenShelf} live={live}/>

            {/* LOW STOCK WALL */}
            <LowStockWall items={lowWall}/>
          </div>

          {/* RIGHT · Activity + Heatmap */}
          <div className="space-y-4 min-w-0">
            <Heatmap stores={stores}/>
            <ActivityFeed items={recent}/>
          </div>
        </div>
      </div>

      {/* Overlays */}
      {openShelf && <ShelfDrawer shelf={openShelf} onClose={() => setOpenShelf(null)}/>}
      {searchOpen && (
        <SearchOverlay q={q} setQ={setQ} result={searchRes}
          onClose={() => { setSearchOpen(false); setQ(""); setSearchRes(null); }}
          onPickStore={(id) => { setSelectedId(id); setSearchOpen(false); setQ(""); }}
        />
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════
// KPI STRIP
// ══════════════════════════════════════════════════════════════════════
function KpiStrip({ kpis }) {
  const cards = [
    { key: "value",       label: "Inventory Value", icon: Boxes,          value: kpis.inventory_value,   fmt: fmtINRShort, tone: "gold"   },
    { key: "low",         label: "Low Stock",       icon: AlertTriangle,  value: kpis.low_stock_count,                     tone: "amber"  },
    { key: "critical",    label: "Critical",        icon: Flame,          value: kpis.critical_count,                      tone: "rose"   },
    { key: "incoming",    label: "Incoming",        icon: Truck,          value: kpis.incoming_pos,      sub: fmtINRShort(kpis.incoming_value), tone: "sky" },
    { key: "transfers",   label: "Transfers Today", icon: ArrowRightLeft, value: kpis.transfers_today,                     tone: "violet" },
    { key: "consumption", label: "Consumed Today",  icon: ArrowDownToLine, value: kpis.consumption_today,                   tone: "emerald"},
    { key: "receipts",    label: "Receipts Today",  icon: Package,        value: kpis.receipts_today,                      tone: "cyan"   },
    { key: "dead",        label: "Dead Stock",      icon: Snowflake,      value: kpis.dead_stock_count,                    tone: "slate"  },
  ];
  const toneMap = {
    gold:    "text-hocp-gold",
    amber:   "text-amber-300",
    rose:    "text-rose-300",
    sky:     "text-sky-300",
    violet:  "text-violet-300",
    emerald: "text-emerald-300",
    cyan:    "text-cyan-300",
    slate:   "text-slate-400",
  };
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
      {cards.map(c => {
        const Icon = c.icon;
        return (
          <div key={c.key} data-testid={`dt-kpi-${c.key}`}
            className="group rounded-2xl border border-white/[0.10] bg-[#0F1530]/70 hover:bg-[#141C3D]/80 backdrop-blur-md p-3.5 transition-all hover:border-white/20">
            <div className="flex items-center justify-between mb-2.5">
              <Icon className={`w-3.5 h-3.5 ${toneMap[c.tone]}`} strokeWidth={2}/>
              <span className="text-[9.5px] uppercase tracking-widest text-white/40">{c.label}</span>
            </div>
            <div className="font-display text-[26px] font-semibold leading-none tabular-nums">
              {c.fmt ? c.fmt(c.value || 0) : <CountUp value={c.value || 0}/>}
            </div>
            {c.sub && <div className="text-[11px] text-white/40 mt-1.5">{c.sub}</div>}
          </div>
        );
      })}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════
// STORE SELECTOR (left sidebar)
// ══════════════════════════════════════════════════════════════════════
function StoreSelector({ stores, selectedId, onSelect }) {
  return (
    <aside className="rounded-3xl border border-white/[0.10] bg-[#0F1530]/70 backdrop-blur-md p-3 max-h-[85vh] overflow-y-auto"
      data-testid="dt-store-selector">
      <div className="px-2 py-1.5 flex items-center gap-2">
        <Warehouse className="w-3.5 h-3.5 text-white/50"/>
        <span className="text-[10.5px] uppercase tracking-widest text-white/40">Stores</span>
        <span className="ml-auto text-[10.5px] text-white/40 tabular-nums">{stores.length}</span>
      </div>
      <div className="space-y-1 mt-1">
        {stores.length === 0 && (
          <div className="text-[12px] text-white/40 px-2 py-8 text-center">No stores yet.</div>
        )}
        {stores.map(st => {
          const h = HEALTH[st.health?.status] || HEALTH.healthy;
          const active = st.id === selectedId;
          return (
            <button key={st.id} onClick={() => onSelect(st.id)}
              data-testid={`dt-store-${st.id}`}
              className={`w-full text-left rounded-xl px-3 py-2.5 transition-all group relative overflow-hidden ${
                active ? "bg-white/[0.08] ring-1 ring-white/20" : "hover:bg-white/[0.04]"}`}>
              <div className="flex items-center gap-2.5">
                <span className={`w-1.5 h-8 rounded-full ${h.bar.includes("emerald") ? "bg-emerald-400" : h.bar.includes("amber") ? "bg-amber-400" : h.bar.includes("rose") ? "bg-rose-400" : "bg-slate-400"}`}/>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium text-white truncate">{st.name}</div>
                  <div className="text-[10.5px] text-white/40 flex items-center gap-2 mt-0.5">
                    <span className="mono">{st.code}</span>
                    <span>·</span>
                    <span>{st.item_count} items</span>
                  </div>
                </div>
                {active && <ChevronRight className="w-3.5 h-3.5 text-white/60"/>}
              </div>
              <div className="mt-2 flex items-center gap-2 text-[10.5px]">
                <span className={`px-1.5 py-0.5 rounded ${h.chip}`}>{h.label}</span>
                <span className="text-white/40">{fmtINRShort(st.value)}</span>
              </div>
            </button>
          );
        })}
      </div>
    </aside>
  );
}


// ══════════════════════════════════════════════════════════════════════
// STORE VISUALIZATION (center)
// ══════════════════════════════════════════════════════════════════════
function StoreVisualization({ data, onShelfClick, live }) {
  if (!data) return (
    <div className="rounded-3xl border border-white/[0.10] bg-[#0F1530]/70 backdrop-blur-md p-10 text-center text-[13px] text-white/40">
      Pick a store to load its digital twin →
    </div>
  );
  return (
    <div className="rounded-3xl border border-white/[0.10] bg-[#0F1530]/70 backdrop-blur-md overflow-hidden"
      data-testid="dt-store-visualization">
      {/* Header */}
      <div className="px-5 py-4 border-b border-white/[0.08] flex items-end gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Warehouse className="w-3.5 h-3.5 text-white/50"/>
            <span className="text-[10.5px] uppercase tracking-widest text-white/40">Store</span>
          </div>
          <h2 className="font-display text-[22px] font-semibold leading-none">{data.store.name}</h2>
          <div className="text-[11.5px] text-white/50 mt-1.5 mono">{data.store.code}</div>
        </div>
        <div className="ml-auto grid grid-cols-3 gap-6 text-right">
          <Metric label="Shelves" value={data.shelves.length}/>
          <Metric label="Items" value={data.total_item_count}/>
          <Metric label="Value" value={fmtINRShort(data.total_value)} raw/>
        </div>
      </div>

      {/* Live Movement banner */}
      {live?.length > 0 && <LiveMovementBanner live={live.slice(0, 3)}/>}

      {/* Shelves grid */}
      <div className="p-5">
        {data.shelves.length === 0 ? (
          <div className="text-center py-16 text-white/40 text-[13px]">
            No stock in this store yet. Adjust opening balance or receive a PO.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.shelves.map(sh => (
              <ShelfCard key={sh.category_id} shelf={sh} onClick={() => onShelfClick(sh)}/>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, raw }) {
  return (
    <div>
      <div className="text-[9.5px] uppercase tracking-widest text-white/40">{label}</div>
      <div className="font-display text-[18px] font-semibold tabular-nums mt-0.5">
        {raw ? value : <CountUp value={value || 0}/>}
      </div>
    </div>
  );
}

function ShelfCard({ shelf, onClick }) {
  const h = HEALTH[shelf.health] || HEALTH.healthy;
  return (
    <button onClick={onClick} data-testid={`dt-shelf-${shelf.category_id}`}
      className={`text-left rounded-2xl border border-white/[0.10] bg-[#0B1128]/80 hover:bg-[#101838] p-4 transition-all group hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/20 ${h.ring} ring-1 ring-inset`}>
      <div className="flex items-start justify-between mb-3">
        <div className="min-w-0 flex-1">
          <div className="text-[10.5px] uppercase tracking-widest text-white/40">{shelf.category_code}</div>
          <div className="font-display text-[15px] font-semibold text-white mt-0.5 truncate">{shelf.category_name}</div>
        </div>
        <span className={`px-2 py-0.5 rounded-md text-[10.5px] font-medium ${h.chip}`}>{h.label}</span>
      </div>

      {/* Fill bar */}
      <div className="relative h-2 rounded-full bg-white/[0.06] overflow-hidden mb-3">
        <div className={`absolute inset-y-0 left-0 rounded-full bg-gradient-to-r ${h.bar} transition-all duration-1000`}
             style={{ width: `${Math.max(4, shelf.fill_pct)}%` }}/>
      </div>

      {/* Counts */}
      <div className="grid grid-cols-4 gap-1.5 text-[10.5px] mb-3">
        <Pill n={shelf.counts.healthy}  label="OK"       cls="bg-emerald-500/10 text-emerald-300"/>
        <Pill n={shelf.counts.low}      label="Low"      cls="bg-amber-500/10 text-amber-300"/>
        <Pill n={shelf.counts.critical} label="Critical" cls="bg-rose-500/10 text-rose-300"/>
        <Pill n={shelf.counts.empty}    label="Empty"    cls="bg-slate-500/10 text-slate-300"/>
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-white/[0.06]">
        <div className="text-[11px] text-white/40">
          {shelf.item_count} items · <span className="text-white/60">{fmtINRShort(shelf.value)}</span>
        </div>
        <span className="text-[11px] text-white/40 group-hover:text-white transition-colors flex items-center gap-1">
          Open <ChevronRight className="w-3 h-3"/>
        </span>
      </div>
    </button>
  );
}

function Pill({ n, label, cls }) {
  return (
    <div className={`rounded-md px-1.5 py-1 text-center ${cls}`}>
      <div className="font-semibold tabular-nums">{n}</div>
      <div className="text-[9px] uppercase tracking-wider opacity-70">{label}</div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════
// LIVE MOVEMENT BANNER (animated transfer arrows)
// ══════════════════════════════════════════════════════════════════════
function LiveMovementBanner({ live }) {
  return (
    <div className="px-5 py-2.5 border-b border-white/[0.06] bg-white/[0.02] flex items-center gap-4 overflow-x-auto"
      data-testid="dt-live-movement">
      <div className="flex items-center gap-1.5 shrink-0">
        <Radio className="w-3 h-3 text-emerald-400 animate-pulse"/>
        <span className="text-[10.5px] uppercase tracking-widest text-white/50">Live moves</span>
      </div>
      {live.map((m, i) => (
        <div key={m.ref_id || i} className="flex items-center gap-2 text-[11.5px] whitespace-nowrap animate-fade-in"
             style={{ animation: `slideInLeft 500ms ease-out ${i * 100}ms both` }}>
          <span className="px-2 py-0.5 rounded-md bg-sky-500/10 text-sky-300">{m.from_store_name}</span>
          <ArrowRightLeft className="w-3 h-3 text-white/50"/>
          <span className="px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-300">{m.to_store_name}</span>
          <span className="text-white/70">{m.qty} {m.uom}</span>
          <span className="text-white/40 truncate max-w-[120px]">· {m.item_name}</span>
        </div>
      ))}
      <style>{`
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-8px); }
          to   { opacity: 1; transform: translateX(0);   }
        }
      `}</style>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════
// LOW STOCK WALL
// ══════════════════════════════════════════════════════════════════════
function LowStockWall({ items }) {
  return (
    <div className="rounded-3xl border border-white/[0.10] bg-[#0F1530]/70 backdrop-blur-md overflow-hidden"
      data-testid="dt-low-stock-wall">
      <div className="px-5 py-4 border-b border-white/[0.08] flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-rose-500/15 flex items-center justify-center">
          <ShieldAlert className="w-4 h-4 text-rose-300"/>
        </div>
        <div>
          <h2 className="font-display text-[15px] font-semibold">Low Stock Wall</h2>
          <p className="text-[11px] text-white/50">Items below reorder level across all stores.</p>
        </div>
        <span className="ml-auto text-[11px] text-white/40 tabular-nums">{items.length} items</span>
      </div>

      {items.length === 0 ? (
        <div className="p-10 text-center text-[13px] text-white/40">All stock levels healthy. Nothing to reorder.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 p-4">
          {items.map((it, i) => (
            <div key={i} data-testid={`dt-low-item-${it.item_id}`}
              className={`rounded-xl border p-3 transition-all group ${
                it.severity === "critical"
                  ? "border-rose-500/30 bg-rose-500/[0.05] hover:bg-rose-500/[0.08]"
                  : "border-amber-500/25 bg-amber-500/[0.04] hover:bg-amber-500/[0.07]"}`}>
              <div className="flex items-start justify-between mb-1.5">
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium text-white truncate">{it.item_name}</div>
                  <div className="text-[10.5px] mono text-white/40 mt-0.5">{it.item_sku} · {it.store_name}</div>
                </div>
                <Zap className={`w-3.5 h-3.5 shrink-0 ml-2 ${it.severity === "critical" ? "text-rose-400" : "text-amber-400"}`}/>
              </div>
              <div className="flex items-center justify-between text-[11px]">
                <span className={`tabular-nums font-semibold ${it.severity === "critical" ? "text-rose-300" : "text-amber-300"}`}>
                  {it.qty_on_hand} {it.uom}
                </span>
                <span className="text-white/40">reorder ≤ {it.reorder_level}</span>
              </div>
              <button data-testid={`dt-generate-pr-${it.item_id}`}
                onClick={() => toast.info(`Draft PR ready for ${it.reorder_qty || 10} × ${it.item_name}`, {
                  description: "Click 'Requests' tab in Supply OS to submit.",
                })}
                className="mt-2 w-full text-[11px] font-medium py-1.5 rounded-md bg-white/[0.06] hover:bg-white/10 text-white/80 flex items-center justify-center gap-1.5 transition-colors">
                <ShoppingCart className="w-3 h-3"/> Generate PR ({it.reorder_qty || 10} {it.uom})
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════
// ACTIVITY FEED
// ══════════════════════════════════════════════════════════════════════
const MOVEMENT_META = {
  opening:      { icon: Package,        cls: "text-sky-300 bg-sky-500/10",       label: "Opening"      },
  adjust:       { icon: AlertTriangle,  cls: "text-amber-300 bg-amber-500/10",   label: "Adjust"       },
  receipt:      { icon: Truck,          cls: "text-emerald-300 bg-emerald-500/10", label: "Receipt"      },
  issue:        { icon: ArrowDownToLine, cls: "text-violet-300 bg-violet-500/10", label: "Issue"        },
  transfer_in:  { icon: ArrowRightLeft, cls: "text-cyan-300 bg-cyan-500/10",     label: "Transfer In"  },
  transfer_out: { icon: ArrowRightLeft, cls: "text-cyan-300 bg-cyan-500/10",     label: "Transfer Out" },
};

function ActivityFeed({ items }) {
  return (
    <div className="rounded-3xl border border-white/[0.10] bg-[#0F1530]/70 backdrop-blur-md overflow-hidden max-h-[560px] flex flex-col"
      data-testid="dt-activity-feed">
      <div className="px-4 py-3.5 border-b border-white/[0.08] flex items-center gap-2.5">
        <Radio className="w-3.5 h-3.5 text-emerald-400 animate-pulse"/>
        <span className="font-display text-[13px] font-semibold">Activity Feed</span>
        <span className="ml-auto text-[10.5px] text-white/40 tabular-nums">{items.length}</span>
      </div>
      <div className="overflow-y-auto flex-1 divide-y divide-white/[0.05]">
        {items.length === 0 ? (
          <div className="p-8 text-center text-[12px] text-white/40">No activity yet.</div>
        ) : items.map((m, i) => {
          const meta = MOVEMENT_META[m.type] || MOVEMENT_META.adjust;
          const Icon = meta.icon;
          const t = (m.at || "").slice(11, 16);
          const isPositive = Number(m.qty) > 0;
          return (
            <div key={m.id || i} data-testid={`dt-activity-${i}`}
              className="px-4 py-2.5 flex items-start gap-2.5 hover:bg-white/[0.02] transition-colors">
              <div className={`w-6 h-6 rounded-md shrink-0 flex items-center justify-center ${meta.cls}`}>
                <Icon className="w-3 h-3" strokeWidth={2.25}/>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[12px] text-white truncate">
                  <span className="font-medium">{meta.label}</span>
                  <span className={`ml-2 mono tabular-nums ${isPositive ? "text-emerald-300" : "text-rose-300"}`}>
                    {isPositive ? "+" : ""}{m.qty} {m.item_uom || ""}
                  </span>
                </div>
                <div className="text-[10.5px] text-white/50 truncate mt-0.5">
                  {m.item_name || m.item_sku} · {m.store_name}
                </div>
              </div>
              <span className="text-[10.5px] text-white/30 tabular-nums shrink-0">{t}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════
// HEATMAP
// ══════════════════════════════════════════════════════════════════════
function Heatmap({ stores }) {
  return (
    <div className="rounded-3xl border border-white/[0.10] bg-[#0F1530]/70 backdrop-blur-md overflow-hidden"
      data-testid="dt-heatmap">
      <div className="px-4 py-3.5 border-b border-white/[0.08]">
        <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-widest text-white/40">
          <Flame className="w-3.5 h-3.5"/> Store Heatmap
        </div>
      </div>
      <div className="p-4 grid grid-cols-3 gap-2">
        {stores.map(st => {
          const status = st.health?.status || "healthy";
          const h = HEALTH[status] || HEALTH.healthy;
          const bg = status === "healthy" ? "bg-emerald-500/20 hover:bg-emerald-500/30"
                : status === "warning"  ? "bg-amber-500/20 hover:bg-amber-500/30"
                : status === "critical" ? "bg-rose-500/25 hover:bg-rose-500/35"
                :                          "bg-slate-500/20 hover:bg-slate-500/30";
          return (
            <div key={st.id} data-testid={`dt-heatmap-${st.id}`}
              className={`aspect-square rounded-lg ${bg} p-2 flex flex-col justify-between transition-colors cursor-pointer ${h.ring} ring-1 ring-inset`}>
              <div className="text-[9px] mono uppercase text-white/60 truncate">{st.code}</div>
              <div>
                <div className="text-[18px] font-semibold tabular-nums leading-none">{st.health?.score ?? "—"}</div>
                <div className="text-[9px] text-white/50 mt-0.5 truncate">{st.name}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════
// SHELF DRAWER
// ══════════════════════════════════════════════════════════════════════
function ShelfDrawer({ shelf, onClose }) {
  const h = HEALTH[shelf.health] || HEALTH.healthy;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-stretch justify-end"
      onClick={onClose} data-testid="dt-shelf-drawer">
      <div className="bg-[#0B1128] border-l border-white/[0.10] w-full max-w-xl overflow-y-auto"
        onClick={e => e.stopPropagation()}
        style={{ animation: "slideInRight 260ms cubic-bezier(0.16, 1, 0.3, 1)" }}>
        <div className="sticky top-0 px-5 py-4 border-b border-white/[0.08] bg-[#0B1128]/95 backdrop-blur-md flex items-start gap-3 z-10">
          <div className={`w-10 h-10 rounded-xl bg-white/[0.06] ${h.ring} ring-1 ring-inset flex items-center justify-center shrink-0`}>
            <Boxes className={`w-4 h-4 ${h.text}`}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] uppercase tracking-widest text-white/40">{shelf.category_code}</div>
            <h3 className="font-display text-[18px] font-semibold text-white leading-none mt-0.5">{shelf.category_name}</h3>
            <div className="text-[11.5px] text-white/50 mt-1.5">
              {shelf.item_count} items · {fmtINR(shelf.value)}
              <span className={`ml-2 px-1.5 py-0.5 rounded ${h.chip}`}>{h.label}</span>
            </div>
          </div>
          <button onClick={onClose} data-testid="dt-shelf-close"
            className="p-1.5 rounded-lg hover:bg-white/10 text-white/60">
            <X className="w-4 h-4"/>
          </button>
        </div>

        <div className="p-5 space-y-2">
          {shelf.items.map(it => {
            const ih = HEALTH[it.health] || HEALTH.healthy;
            return (
              <div key={it.stock_id} data-testid={`dt-drawer-item-${it.stock_id}`}
                className={`rounded-xl border border-white/[0.08] bg-white/[0.03] p-3.5 ${ih.ring} ring-1 ring-inset`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[13.5px] font-medium text-white truncate">{it.name}</div>
                    <div className="text-[10.5px] mono text-white/40 mt-0.5">{it.sku}</div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-md text-[10.5px] font-medium ${ih.chip}`}>{ih.label}</span>
                </div>
                <div className="grid grid-cols-4 gap-2 text-[11px] mb-2.5">
                  <Stat label="On hand" value={`${it.qty_on_hand} ${it.uom}`}/>
                  <Stat label="Reorder" value={`${it.reorder_level} ${it.uom}`}/>
                  <Stat label="Avg cost" value={fmtINR(it.avg_cost)}/>
                  <Stat label="Value" value={fmtINR(it.value)}/>
                </div>
                <div className="relative h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                  <div className={`absolute inset-y-0 left-0 rounded-full bg-gradient-to-r ${ih.bar} transition-all duration-1000`}
                       style={{ width: `${Math.max(3, it.fill_pct)}%` }}/>
                </div>
              </div>
            );
          })}
          {shelf.items.length === 0 && (
            <div className="text-center py-12 text-white/40 text-[13px]">Shelf empty.</div>
          )}
        </div>

        <style>{`
          @keyframes slideInRight {
            from { transform: translateX(24px); opacity: 0; }
            to   { transform: translateX(0);    opacity: 1; }
          }
        `}</style>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[9.5px] uppercase tracking-widest text-white/40">{label}</div>
      <div className="tabular-nums text-white mt-0.5">{value}</div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════
// SEARCH OVERLAY (Cmd+K)
// ══════════════════════════════════════════════════════════════════════
function SearchOverlay({ q, setQ, result, onClose, onPickStore }) {
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-start justify-center pt-24 px-4"
      onClick={onClose} data-testid="dt-search-overlay">
      <div className="w-full max-w-2xl rounded-2xl bg-[#0B1128] border border-white/[0.14] overflow-hidden"
        onClick={e => e.stopPropagation()}
        style={{ animation: "fadeInDown 260ms cubic-bezier(0.16, 1, 0.3, 1)" }}>
        <div className="px-4 py-3 border-b border-white/[0.08] flex items-center gap-3">
          <Search className="w-4 h-4 text-white/50"/>
          <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search items, vendors, stores…"
            data-testid="dt-search-input"
            className="flex-1 bg-transparent outline-none text-[14px] text-white placeholder-white/30"/>
          <kbd className="px-1.5 py-0.5 rounded bg-white/[0.06] text-[10px] text-white/50">ESC</kbd>
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-3 space-y-4">
          {!q && (
            <div className="text-center text-[12px] text-white/40 py-8">
              Start typing to search across items, vendors and stores.
            </div>
          )}
          {q && !result && (
            <div className="text-center text-[12px] text-white/40 py-6">Searching…</div>
          )}
          {result && (
            <>
              <SearchGroup title="Items" items={result.items || []} render={(x) => (
                <><span className="text-white">{x.name}</span> <span className="mono text-white/40">· {x.sku}</span></>
              )}/>
              <SearchGroup title="Vendors" items={result.vendors || []} render={(x) => (
                <><span className="text-white">{x.name}</span> <span className="mono text-white/40">· {x.code}</span></>
              )}/>
              <SearchGroup title="Stores" items={result.stores || []} render={(x) => (
                <><span className="text-white">{x.name}</span> <span className="mono text-white/40">· {x.code}</span></>
              )} onPick={(x) => onPickStore(x.id)}/>
              {!(result.items?.length || result.vendors?.length || result.stores?.length) && (
                <div className="text-center text-[12px] text-white/40 py-6">No matches for &ldquo;{q}&rdquo;.</div>
              )}
            </>
          )}
        </div>
        <style>{`
          @keyframes fadeInDown {
            from { opacity: 0; transform: translateY(-8px); }
            to   { opacity: 1; transform: translateY(0);   }
          }
        `}</style>
      </div>
    </div>
  );
}

function SearchGroup({ title, items, render, onPick }) {
  if (!items?.length) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-white/40 px-1.5 mb-1.5">{title}</div>
      <div className="space-y-1">
        {items.map(it => (
          <button key={it.id} onClick={() => onPick && onPick(it)}
            data-testid={`dt-search-hit-${it.id}`}
            className="w-full text-left rounded-lg px-3 py-2 hover:bg-white/[0.05] text-[12.5px] flex items-center justify-between transition-colors">
            {render(it)}
            <ChevronRight className="w-3 h-3 text-white/30"/>
          </button>
        ))}
      </div>
    </div>
  );
}
